import { Injectable } from '@angular/core';
import {
  Observable,
  shareReplay,
  map,
  of,
  tap,
  forkJoin
} from 'rxjs';
import { VocabApiService } from '../../vocab/vocab.service';

@Injectable({
  providedIn: 'root',
})
export class VocabHelperService {
  private cache = new Map<string, any>();
  private inFlightRequests = new Map<string, Observable<any>>();

  constructor(private vocabService: VocabApiService) {}

  // Use your vocabService.getVocabData API call here
  private fetchFromAPI(
    keys: string[]
  ): Observable<Array<{ vocabularyName: string; vocabularyItems: any[] }>> {
    return this.vocabService.getVocabData(keys); // assuming it already returns the array in desired shape
  }

  getVocabData(
    keys: string[]
  ): Observable<Array<{ vocabularyName: string; vocabularyItems: any[] }>> {
    const cachedEntries: Array<{
      vocabularyName: string;
      vocabularyItems: any[];
    }> = [];
    const observables: Observable<Record<string, any[]>>[] = [];
    const keysToFetch: string[] = [];
    for (const key of keys) {
      if (this.cache.has(key)) {
        cachedEntries.push({
          vocabularyName: key,
          vocabularyItems: this.cache.get(key),
        });
      } else if (this.inFlightRequests.has(key)) {
        observables.push(
          this.inFlightRequests.get(key)!.pipe(map((data) => ({ [key]: data })))
        );
      } else {
        keysToFetch.push(key);
      }
    }

    if (keysToFetch.length > 0) {
      const apiCall$ = this.fetchFromAPI(keysToFetch).pipe(
        tap((fetchedArray) => {
          for (const item of fetchedArray) {
            this.cache.set(item.vocabularyName, item.vocabularyItems);
          }
          for (const key of keysToFetch) {
            this.inFlightRequests.delete(key);
          }
        }),
        map((fetchedArray) => {
          // Convert array into object so individual keys can be used in inflightRequests
          const obj: Record<string, any[]> = {};
          for (const item of fetchedArray) {
            obj[item.vocabularyName] = item.vocabularyItems;
          }
          return obj;
        }),
        shareReplay(1)
      );

      for (const key of keysToFetch) {
        this.inFlightRequests.set(key, apiCall$.pipe(map((res) => res[key])));
      }

      observables.push(apiCall$);
    }

    if (observables.length === 0) {
      // All from cache
      return of(cachedEntries);
    }

    return forkJoin(observables).pipe(
      map((resultsArray) => {
        const mergedResult: Array<{
          vocabularyName: string;
          vocabularyItems: any[];
        }> = [...cachedEntries];

        for (const result of resultsArray) {
          for (const [key, items] of Object.entries(result)) {
            mergedResult.push({
              vocabularyName: key,
              vocabularyItems: items,
            });
          }
        }

        return mergedResult;
      })
    );
  }
}
